/**
 * GRI v2 Module - Main Container
 * Comprehensive GRI Reporting System
 */

import React, { useState } from 'react';
import GRIv2Workspace from './GRIv2Workspace';
import GRIDisclosureIndex from './GRIDisclosureIndex';
import GRIGeneralDisclosures from './GRIGeneralDisclosures';
import GRIStandardsNavigator from './GRIStandardsNavigator';
import GRICSRDModule from './GRICSRDModule'; // v1 Materiality
import GRIAICommentary from './GRIAICommentary';
import {
  LayoutDashboard, List, FileText, BookOpen, Target, ArrowLeft, Brain
} from 'lucide-react';

const GRIv2Module = ({ study }) => {
  const [activeView, setActiveView] = useState('workspace'); // workspace, disclosure-index, general-disclosures, standards-navigator, materiality, ai-commentary

  const renderView = () => {
    switch (activeView) {
      case 'workspace':
        return <GRIv2Workspace study={study} onNavigate={setActiveView} />;
      case 'disclosure-index':
        return <GRIDisclosureIndex study={study} />;
      case 'general-disclosures':
        return <GRIGeneralDisclosures study={study} />;
      case 'standards-navigator':
        return <GRIStandardsNavigator study={study} />;
      case 'materiality':
        return <GRICSRDModule study={study} />;
      case 'ai-commentary':
        return <GRIAICommentary study={study} />;
      default:
        return <GRIv2Workspace study={study} onNavigate={setActiveView} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Navigation Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 p-2">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveView('workspace')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
              activeView === 'workspace'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <LayoutDashboard className="w-4 h-4" />
            Workspace
          </button>

          <button
            onClick={() => setActiveView('materiality')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
              activeView === 'materiality'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Target className="w-4 h-4" />
            Material Topics
          </button>

          <button
            onClick={() => setActiveView('disclosure-index')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
              activeView === 'disclosure-index'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <List className="w-4 h-4" />
            Disclosure Index
          </button>

          <button
            onClick={() => setActiveView('general-disclosures')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
              activeView === 'general-disclosures'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <FileText className="w-4 h-4" />
            GRI 2
          </button>

          <button
            onClick={() => setActiveView('standards-navigator')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
              activeView === 'standards-navigator'
                ? 'bg-teal-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            Standards Navigator
          </button>

          <button
            onClick={() => setActiveView('ai-commentary')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
              activeView === 'ai-commentary'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Brain className="w-4 h-4" />
            AI Commentary
          </button>
        </div>
      </div>

      {/* Content Area */}
      {renderView()}
    </div>
  );
};

export default GRIv2Module;
